// src/pages/employer/profile/EmployerMedia.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useConfirm } from '../../../components/shared/UI';
import api from '../../../services/api';
import { Image, FileText, Upload, Trash2, Library } from 'lucide-react';
import toast from 'react-hot-toast';

// ─── Section config ────────────────────────────────────────

const MEDIA_SECTIONS = [
  {
    key:    'PHOTO',
    label:  'Images',
    accept: 'image/jpeg,image/png,image/webp,image/svg+xml',
    icon:   Image,
    hint:   'JPEG, PNG, WebP or SVG',
    isDoc:  false,
  },
  {
    key:    'APPLICATION_FORM',
    label:  'Application Forms',
    accept: '.pdf,.doc,.docx,.rtf,.odt',
    icon:   FileText,
    hint:   'PDF or Word document',
    isDoc:  true,
  },
  {
    key:    'JOB_DESCRIPTION',
    label:  'Job Descriptions',
    accept: '.pdf,.doc,.docx,.rtf,.odt',
    icon:   FileText,
    hint:   'PDF or Word document',
    isDoc:  true,
  },
];

// ─── Upload button ─────────────────────────────────────────

const UploadBtn = ({ sectionKey, accept, uploading, setUploading, orgId, profileId, onUploaded }) => {
  const fileRef = useRef();
  const isUploading = uploading === sectionKey;

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(sectionKey);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('type', sectionKey);
      await api.post(`/orgs/${orgId}/profiles/${profileId}/media`, form);
      toast.success('Uploaded');
      onUploaded();
    } catch {
      // axios interceptor handles error toast
    } finally {
      setUploading(null);
      e.target.value = '';
    }
  };

  return (
    <>
      <button
        className="btn btn-outline btn-sm"
        onClick={() => fileRef.current.click()}
        disabled={!!uploading}
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <Upload size={13} />
        {isUploading ? 'Uploading…' : 'Upload'}
      </button>
      <input ref={fileRef} type="file" accept={accept} style={{ display: 'none' }} onChange={handleFile} />
    </>
  );
};

// ─── File list ─────────────────────────────────────────────

const FileList = ({ items, isDoc, onDelete, isAdmin }) => {
  if (!items.length) return (
    <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0, padding: '4px 0' }}>
      Nothing uploaded yet.
    </p>
  );

  if (isDoc) {
    // Documents: row list with name, size, download link
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((m) => (
          <div key={m.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px', borderRadius: 6,
            background: '#F8F9FA', border: '1px solid var(--border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <FileText size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <a href={m.url} target="_blank" rel="noopener noreferrer" style={{
                  fontSize: 13, color: 'var(--secondary)', fontWeight: 500,
                  textDecoration: 'none', display: 'block',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
                  onMouseEnter={(e) => { e.target.style.textDecoration = 'underline'; }}
                  onMouseLeave={(e) => { e.target.style.textDecoration = 'none'; }}
                >
                  {m.name}
                </a>
                {m.fileSize && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {(m.fileSize / 1024).toFixed(0)} KB
                  </span>
                )}
              </div>
            </div>
            {isAdmin && (
              <button onClick={() => onDelete(m.id)} title="Delete" style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#CC0000',
                display: 'flex', alignItems: 'center', padding: '2px 4px', flexShrink: 0,
              }}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Images: thumbnail grid
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
      {items.map((m) => (
        <div key={m.id} style={{
          borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden',
          background: '#F8F9FA', position: 'relative',
        }}>
          <img src={m.url} alt={m.name}
            style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
          <div style={{ padding: '6px 8px' }}>
            <div style={{ fontSize: 11, color: 'var(--text)', fontWeight: 500,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {m.name}
            </div>
            {m.fileSize && (
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                {(m.fileSize / 1024).toFixed(0)} KB
              </div>
            )}
          </div>
          {isAdmin && (
            <button onClick={() => onDelete(m.id)} title="Delete" style={{
              position: 'absolute', top: 6, right: 6,
              background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: 4,
              padding: '3px 5px', cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}>
              <Trash2 size={11} style={{ color: 'white' }} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────

export const EmployerMedia = () => {
  const { activeOrg, activeProfile, isAdmin } = useAuth();
  const { confirm, dialog }                   = useConfirm();
  const [media, setMedia]                     = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [uploading, setUploading]             = useState(null);

  const orgId     = activeOrg?.id;
  const profileId = activeProfile?.id;

  const loadMedia = () => {
    if (!orgId || !profileId) { setLoading(false); return; }
    api.get(`/orgs/${orgId}/profiles/${profileId}/media`)
      .then(({ data }) => setMedia(data.data.media || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadMedia(); }, [orgId, profileId]);

  const handleDelete = async (mediaId, name) => {
    const ok = await confirm('Delete File', `Delete "${name}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await api.delete(`/orgs/${orgId}/profiles/${profileId}/media/${mediaId}`);
      toast.success('Deleted');
      setMedia((m) => m.filter((x) => x.id !== mediaId));
    } catch {
      // axios interceptor handles error toast
    }
  };

  if (!activeOrg) return (
    <div className="fade-in">
      <div className="page-header"><h1>Media Library</h1></div>
      <div style={{ padding: 40, color: 'var(--text-muted)', textAlign: 'center' }}>No active profile selected.</div>
    </div>
  );

  if (loading) return (
    <div className="fade-in">
      <div className="page-header"><h1>Media Library</h1></div>
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
    </div>
  );

  return (
    <div className="fade-in">
      {dialog}
      <div className="page-header">
        <h1>Media Library</h1>
      </div>

      {MEDIA_SECTIONS.map(({ key, label, accept, icon: Icon, hint, isDoc }) => {
        const items = media.filter((m) => m.type === key);
        return (
          <div key={key} className="section-box" style={{ marginBottom: 16 }}>
            <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <Icon size={14} />
                {label}
                {items.length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.7)', marginLeft: 2 }}>
                    ({items.length})
                  </span>
                )}
              </span>
              {isAdmin && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{hint}</span>
                  <UploadBtn
                    sectionKey={key}
                    accept={accept}
                    uploading={uploading}
                    setUploading={setUploading}
                    orgId={orgId}
                    profileId={profileId}
                    onUploaded={loadMedia}
                  />
                </div>
              )}
            </div>
            <div className="section-box-body">
              <FileList items={items} isDoc={isDoc} onDelete={(id) => handleDelete(id, items.find(m => m.id === id)?.name || 'this file')} isAdmin={isAdmin} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
